import { filing } from './toolkit';

/**
 * JA-10: Petition for a compulsory accounting and related relief (SCPA 2205), with the
 * verification and the compulsory accounting citation. Filed by an interested person
 * (distributee, legatee, creditor...) against the estate's fiduciary, who is the executor
 * of record. Petitioners and their attorney are selected participants.
 */
export default filing('file-1787254291204-c640398b-d2ff-4e55-b187-16f2e77fcc7e.pdf', 'Petition for a compulsory accounting', f => {
  const P = 'topmostSubform[0].Page1[0].';
  const P2 = 'topmostSubform[0].Page2[0].';
  const P3 = 'topmostSubform[0].Page3[0].';
  const P4 = 'topmostSubform[0].Page4[0].';

  // Page 1: caption
  f.court(`${P}TextField1[0]`, 'Filing fee paid ($)');
  f.court(`${P}TextField2[0]`, 'Receipt No.');
  f.shared(`${P}TextField3[0]`, 'estate.surrogateCourtCounty', "Caption: Surrogate's Court, County of");
  f.shared(`${P}TextField4[0]`, 'decedent.fullName', 'Caption: in the Estate of (decedent name)');
  f.shared(`${P}TextField5[0]`, 'estate.courtFileNumber', 'File No.');
  f.shared(`${P}TextField6[0]`, 'estate.surrogateCourtCounty', "To the Surrogate's Court of the County of");

  // 1. Petitioners
  const petitioners = '1. Petitioners';
  f.person('petitioner1', 'Petitioner 1', petitioners,
    'Select the interested person asking for the accounting. Name, domicile and citizenship come from their participant record.');
  f.person('petitioner2', 'Petitioner 2 (optional)', petitioners,
    'Only if a second person joins the petition. Their details come from their participant record.');
  ([['petitioner1', 1, ['7', '8', '9', '10', '11', '12']], ['petitioner2', 2, ['13', '14', '15', '16', '17', '18']]] as const)
    .forEach(([key, n, [name, street, city, state, zip, citizen]]) => {
      f.personField(`${P}TextField${name}[0]`, key, 'fullName', `1. Petitioner ${n}: name`);
      f.filingTemplate(`${P}TextField${street}[0]`, `{{probateFiling.${key}_street}} {{probateFiling.${key}_addressLine2}}`,
        `1. Petitioner ${n}: domicile (street and number)`);
      f.personField(`${P}TextField${city}[0]`, key, 'city', `1. Petitioner ${n}: city, village or town`);
      f.personField(`${P}TextField${state}[0]`, key, 'state', `1. Petitioner ${n}: state`);
      f.personField(`${P}TextField${zip}[0]`, key, 'zipCode', `1. Petitioner ${n}: zip code`);
      f.personField(`${P}TextField${citizen}[0]`, key, 'citizenship', `1. Petitioner ${n}: citizen of`);
    });

  // 2. Death
  f.shared(`${P}TextField19[0]`, 'decedent.dateOfDeath', '2. The decedent died on');

  // 3. Letters
  const letters = '3. Letters';
  f.choice('lettersIssued', '3. Letters granted', letters, [
    { label: 'Testamentary', fields: [`${P}CheckBox1[0]`] },
    { label: 'Of administration', fields: [`${P}CheckBox2[0]`] },
    { label: 'Of trusteeship', fields: [`${P}CheckBox3[0]`] },
    { label: 'Other (specify below)', value: 'Other', fields: [`${P}CheckBox4[0]`] },
  ], 'checkbox', 'Tick the kind of letters held by the fiduciary. It should agree with "Type of letters" on the estate record.');
  f.input(`${P}TextField20[0]`, 'lettersOtherType', '3. Other letters (specify)', letters, 'text',
    'Only when "Other" is ticked, for example letters of temporary administration.');
  f.shared(`${P}TextField21[0]`, 'estate.surrogateCourtCounty', "3. Letters granted by the Surrogate's Court of the County of");
  f.shared(`${P}TextField22[0]`, 'estate.dateOfLetters', '3. Letters granted on (date)');
  f.notApplicable(`${P}TextField23[0]`, '3. Letters granted on (year)', 'The full date of letters, including the year, prints in the previous box.');
  f.shared(`${P}TextField24[0]`, 'executor.fullName', '3. Letters granted to (fiduciary name)');
  f.shared(`${P}TextField25[0]`, 'executor.fullAddress', '3. Fiduciary residing at');

  // 5. Interest and reason
  const interest = '5. Interest and reason';
  ([['petitionerIsDistributee', 'Distributee', 5], ['petitionerIsLegatee', 'Legatee', 6],
    ['petitionerIsCreditor', 'Creditor', 7], ['petitionerIsOther', 'Other interest (specify below)', 8]] as const)
    .forEach(([key, label, box]) => f.choice(key, `5. The petitioner(s) is/are: ${label}`, interest, [
      { label: 'Yes', fields: [`${P}CheckBox${box}[0]`] },
      { label: 'No', fields: [] },
    ], 'checkbox'));
  f.input(`${P}TextField26[0]`, 'petitionerOtherInterest', '5. Other interest (specify)', interest);
  [27, 28, 29, 30].forEach((n, i) => f.input(`${P}TextField${n}[0]`, `accountingReason${i + 1}`,
    `5. Reason the petitioner(s) wish the fiduciary to account: line ${i + 1}`, interest, 'text',
    i === 0 ? 'Why the fiduciary should be compelled to account. Continue on the next lines if needed.' : undefined));

  // Page 2: 6. Persons entitled to notice
  f.people('noticeParties', '6. Persons entitled to notice (SCPA 2206(2))', '6. Persons entitled to notice', 6, 'Nature of interest',
    'Select each person entitled to notice. Name and address come from Participants; enter their nature of interest.');
  for (let i = 0; i < 6; i++) {
    const [name, address, nature] = [31 + i * 3, 32 + i * 3, 33 + i * 3];
    f.peopleField(`${P2}TextField${name}[0]`, 'noticeParties', i, 'fullName', `6. Person ${i + 1}: name`);
    f.peopleField(`${P2}TextField${address}[0]`, 'noticeParties', i, 'fullAddress', `6. Person ${i + 1}: address`);
    f.peopleField(`${P2}TextField${nature}[0]`, 'noticeParties', i, 'note', `6. Person ${i + 1}: nature of interest`);
  }

  // Wherefore and additional relief
  f.shared(`${P2}TextField49[0]`, 'executor.fullName', 'Wherefore: process issue requiring (name of fiduciary)');
  const relief = 'Additional relief (SCPA 2205, 2206)';
  f.choice('reliefSuspendAndAppoint', 'Additional relief: on failure to appear or account, suspend letters, appoint a temporary fiduciary and set a removal hearing', relief, [
    { label: 'Yes', fields: [`${P2}CheckBox9[0]`, `${P4}CheckBox11[0]`] },
    { label: 'No', fields: [] },
  ], 'checkbox', 'Printed on the petition and on the citation.');
  f.person('temporaryFiduciary', 'Eligible person to be appointed temporary fiduciary', relief,
    'Only if the relief above is requested. Select the participant.');
  f.personField(`${P2}TextField50[0]`, 'temporaryFiduciary', 'fullName', 'Additional relief: eligible person(s) as temporary fiduciary');
  f.shared(`${P2}TextField51[0]`, 'executor.fullName', 'Additional relief: hearing on the removal of (fiduciary whose letters are suspended)');
  f.person('successorFiduciary', 'Person to be appointed successor fiduciary', relief,
    'Only if the relief above is requested. Select the participant.');
  f.personField(`${P2}TextField52[0]`, 'successorFiduciary', 'fullName', 'Additional relief: appointment of (successor fiduciary)');
  f.choice('reliefHearingOnProposedAccount', "Additional relief: if letters are suspended or the fiduciary fails to account, fix a hearing to take and state the fiduciary's account from the proposed accounting attached", relief, [
    { label: 'Yes', fields: [`${P2}CheckBox10[0]`, `${P4}CheckBox12[0]`] },
    { label: 'No', fields: [] },
  ], 'checkbox', 'Only if a proposed accounting is attached and its summary statement is served. Printed on the petition and on the citation.');
  [53, 54, 55].forEach((n, i) => f.input(`${P2}TextField${n}[0]`, `furtherRelief${i + 1}`,
    `Further relief requested: line ${i + 1}`, relief));

  // Execution
  f.signature(`${P2}TextField56[0]`, 'Date signed (completed at signing)');
  f.signature(`${P2}TextField57[0]`, '1. Signature of petitioner');
  f.signature(`${P2}TextField58[0]`, '2. Signature of petitioner');
  f.personField(`${P2}TextField59[0]`, 'petitioner1', 'fullName', '1. Petitioner print name');
  f.personField(`${P2}TextField60[0]`, 'petitioner2', 'fullName', '2. Petitioner print name');
  const corporate = 'Corporate petitioners (a bank or trust company) are not supported: petitioners are selected participants who sign individually.';
  f.notApplicable(`${P2}TextField61[0]`, '3. Name of corporate petitioner', corporate);
  f.notApplicable(`${P2}TextField62[0]`, '3. Signature of officer (corporate petitioner)', corporate);
  f.notApplicable(`${P2}TextField63[0]`, '3. Print name and title of officer (corporate petitioner)', corporate);

  // Page 3: verification by individual petitioners
  f.notary(`${P3}TextField64[0]`, 'Verification: County of (venue where sworn)');
  f.personField(`${P3}TextField65[0]`, 'petitioner1', 'fullName', 'Verification: name of deponent (being duly sworn)');
  f.signature(`${P3}TextField66[0]`, 'Verification: signature of petitioner 1');
  f.signature(`${P3}TextField67[0]`, 'Verification: signature of petitioner 2');
  f.personField(`${P3}TextField68[0]`, 'petitioner1', 'fullName', 'Verification: petitioner 1 print name');
  f.personField(`${P3}TextField69[0]`, 'petitioner2', 'fullName', 'Verification: petitioner 2 print name');
  f.notary(`${P3}TextField70[0]`, 'Jurat: On (date)');
  f.notary(`${P3}TextField71[0]`, 'Jurat: year');
  f.notary(`${P3}TextField72[0]`, 'Jurat: before me personally came (names)');
  f.notary(`${P3}TextField73[0]`, 'Jurat: Notary Public signature and commission');

  // Corporate verification block
  ([
    [74, 'Corporate verification: County of'],
    [75, 'Corporate verification: title of officer'],
    [76, 'Corporate verification: name of bank or trust company'],
    [77, 'Corporate verification: name of corporate petitioner'],
    [78, 'Corporate verification: signature of officer'],
    [79, 'Corporate verification: print name and title of officer'],
    [80, 'Corporate acknowledgment: On (date)'],
    [81, 'Corporate acknowledgment: before me personally came'],
    [82, 'Corporate acknowledgment: resides at'],
    [83, 'Corporate acknowledgment: that he/she is a (title)'],
    [84, 'Corporate acknowledgment: of (corporation)'],
    [85, 'Corporate acknowledgment: Notary Public signature and commission'],
  ] as const).forEach(([n, label]) => f.notApplicable(`${P3}TextField${n}[0]`, label, corporate));

  // Attorney for the petitioner(s)
  const attorney = 'Attorney for petitioner(s)';
  f.person('petitionerAttorney', "Petitioner's attorney", attorney,
    "Select the attorney who represents the petitioner(s). This is usually not the estate's own attorney, who acts for the fiduciary. Name, firm, phone and address come from their participant record.");
  f.signature(`${P3}TextField86[0]`, 'Signature of attorney');
  f.personField(`${P3}TextField87[0]`, 'petitionerAttorney', 'fullName', 'Attorney: print name');
  f.personField(`${P3}TextField88[0]`, 'petitionerAttorney', 'firmName', 'Attorney: firm name');
  f.personField(`${P3}TextField89[0]`, 'petitionerAttorney', 'phone', 'Attorney: tel. no.');
  f.personField(`${P3}TextField90[0]`, 'petitionerAttorney', 'fullAddress', 'Attorney: address');

  // Page 4: compulsory accounting citation
  f.shared(`${P4}TextField91[0]`, 'estate.courtFileNumber', 'Citation: File No.');
  f.shared(`${P4}TextField92[0]`, 'estate.surrogateCourtCounty', "Citation: Surrogate's Court, County");
  f.shared(`${P4}TextField93[0]`, 'executor.fullName', 'Citation: TO (fiduciary cited)');
  f.shared(`${P4}TextField94[0]`, 'executor.fullAddress', 'Citation: TO (fiduciary address)');
  f.people('additionalCitees', 'Citation: other persons cited', 'Citation', 2, undefined,
    'Only if the citation must also be served on others (for example persons entitled to notice of the additional relief). Name, address and citizenship come from Participants.');
  f.peopleField(`${P4}TextField95[0]`, 'additionalCitees', 0, 'line', 'Citation: TO, other person 1 (name; address; citizenship)');
  f.peopleField(`${P4}TextField96[0]`, 'additionalCitees', 1, 'line', 'Citation: TO, other person 2 (name; address; citizenship)');
  f.personField(`${P4}TextField97[0]`, 'petitioner1', 'fullName', 'Citation: a petition having been filed by');
  f.filingTemplate(`${P4}TextField98[0]`, '{{probateFiling.petitioner1_street}} {{probateFiling.petitioner1_addressLine2}}',
    'Citation: petitioner domiciled at (street)');
  f.personField(`${P4}TextField99[0]`, 'petitioner1', 'cityStateZip', 'Citation: petitioner domiciled at (city, state, zip)');
  f.notApplicable(`${P4}TextField100[0]`, 'Citation: petitioner domicile, continuation line', 'The domicile fits on the two previous lines.');
  f.shared(`${P4}TextField101[0]`, 'estate.surrogateCourtCounty', "Citation: cited to show cause before the Surrogate's Court, (county)");
  f.court(`${P4}TextField102[0]`, 'Citation: County at (court address), New York');
  f.court(`${P4}TextField103[0]`, 'Citation: return date');
  f.court(`${P4}TextField104[0]`, "Citation: at (time) o'clock in the forenoon");
  f.shared(`${P4}TextField105[0]`, 'executor.fullName', 'Citation: why (fiduciary) should not file an account');
  f.shared(`${P4}TextField106[0]`, 'decedent.fullName', 'Citation: account in the estate of');
  f.personField(`${P4}TextField107[0]`, 'temporaryFiduciary', 'fullName', 'Citation additional relief: eligible person(s) as temporary fiduciary');
  f.shared(`${P4}TextField108[0]`, 'executor.fullName', 'Citation additional relief: hearing on the removal of');
  f.personField(`${P4}TextField109[0]`, 'successorFiduciary', 'fullName', 'Citation additional relief: appointment of (successor fiduciary)');
  f.court(`${P4}TextField110[0]`, 'Citation: HON. (Surrogate)');
  f.court(`${P4}TextField111[0]`, 'Citation: dated, attested and sealed (date)');
  f.court(`${P4}TextField112[0]`, 'Citation: dated (year)');
  f.court(`${P4}TextField113[0]`, 'Citation: Chief Clerk signature');
  f.court(`${P4}TextField114[0]`, 'Citation: Chief Clerk');
  f.personField(`${P4}TextField115[0]`, 'petitionerAttorney', 'fullName', 'Citation: name of attorney');
  f.personField(`${P4}TextField116[0]`, 'petitionerAttorney', 'phone', 'Citation: attorney telephone no.');
  f.personField(`${P4}TextField117[0]`, 'petitionerAttorney', 'fullAddress', 'Citation: address of attorney');
});
